<template>
  <div class="avatar-editor">
    <div class="avatar-editor-header">
      <div class="avatar-editor-title">
        <h3 v-if="portrait && portrait.cutUrl">
          {{ $t('editPhoto') }}
        </h3>
      </div>
      <button @click="$emit('close')">
        <i class="isif isif-close" />
      </button>
    </div>
    <div class="avatar-editor-body">
      <div
        v-if="portrait && portrait.cutUrl"
        class="full por"
      >
        <ICropper
          ref="ICropper"
          :boundary="cropperOptions.boundary"
          :viewport="cropperOptions.viewport"
          :mouse-wheel-zoom="false"
          :show-zoomer="cropperOptions.showZoomer"
          @cropperReady="cropperReady"
        />
        <div class="cropper-slider-wrapper">
          <span class="cropper-percent">{{ cropperZoom }}</span>
          <input
            ref="cropperZoom"
            v-model="cropperZoom"
            class="cropper-slider"
            type="range"
            min="0"
            :max="maxZoom"
          >
          <span class="cropper-percent">{{ maxZoom }}</span>
        </div>
      </div>
      <div
        v-else
        class="upload-container"
      >
        <button
          class="upload-image-btn"
          @click="triggerImageInput"
        >
          {{ $t('uploadImage') }}
        </button>
      </div>
    </div>
    <div
      v-if="portrait && portrait.cutUrl"
      class="avatar-editor-footer"
    >
      <button
        class="delete-image-btn"
        @click="onDeleteImage"
      >
        {{ $t('deletePhoto') }}
      </button>
      <button
        class="change-image-btn"
        @click="triggerImageInput"
      >
        {{ $t('changePhoto') }}
      </button>
      <button
        class="upload-image-btn"
        @click="onSaveImage"
      >
        {{ $t('savePhoto') }}
      </button>
    </div>
    <input
      id="avatar-file"
      ref="imageInput"
      type="file"
      @change="onChangeImage"
    >
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Message } from 'element-ui'
import { uploadFile } from '@/api/files'
import _ from 'lodash'
import ICropper from './ICropper.vue'

export default {
  components: {
    ICropper
  },

  data () {
    return {
      cropperZoom: 0,
      maxZoom: 10
    }
  },

  computed: {
    ...mapGetters({
      userId: 'auth/objectId',
      profileUrl: 'auth/profileUrl',
      portrait: 'auth/portrait' // portrait could be {} or null
    }),

    cropperOptions () {
      return {
        boundary: {
          width: window.innerWidth > 600 ? 600 : window.innerWidth,
          height: (window.innerWidth > 600 ? 600 : window.innerWidth) * 3 / 5
        },
        viewport: {
          width: 180,
          height: 180,
          type: 'circle'
        },
        showZoomer: false
      }
    }
  },

  watch: {
    cropperZoom (val) {
      this.$refs.ICropper.setZoomViaPercent(val)
      this.$refs.cropperZoom.style.backgroundImage = `linear-gradient(to right, #ec0974 0%, #ec0974 ${val}%, #fff ${val}%, #fff 100%)`
    }
  },

  methods: {
    ...mapActions({
      update: 'auth/update'
    }),

    triggerImageInput () {
      this.$refs.imageInput.click()
    },

    async onChangeImage (e) {
      try {
        if (e.target.files[0]) {
          Message(this.$t('pilotRecorderUploading'))
          const file = e.target.files[0]
          const path = this.userId
          if (file) {
            const profileUrl = await uploadFile(file, path)
            const payload = { profileUrl }
            const updateResult = await this.update(payload)
            this.bindPortraitToCropper(updateResult.portrait)
          }
        }
      } catch (error) {
        console.error(error)
        Message.error(error)
      }
    },

    async onDeleteImage () {
      try {
        const payload = {
          profileUrl: ''
        }
        await this.update(payload)
      } catch (error) {
        console.error(error)
        Message.error(error)
      }
    },

    async onSaveImage () {
      try {
        Message(this.$t('pilotRecorderUploading'))
        const cropperData = this.$refs.ICropper.getCropArea()
        const payload = {
          portrait: {
            cutUrl: this.portrait.cutUrl,
            x: cropperData.x,
            y: cropperData.y,
            height: cropperData.height,
            width: cropperData.width
          }
        }
        await this.update(payload)
        Message.success(this.$t('updateSuccess'))
        this.$emit('close')
      } catch (error) {
        console.error(error)
        Message.error(error)
      }
    },

    cropperReady () {
      this.bindPortraitToCropper(this.portrait)
    },

    bindPortraitToCropper (portrait) {
      const bindOption = {
        url: this.profileUrl
      }
      if (!_.isEmpty(portrait)) {
        if (portrait.width && portrait.height) {
          // 有偵測到人臉
          bindOption.points = [
            portrait.x,
            portrait.y,
            portrait.x + portrait.width,
            portrait.y + portrait.height
          ]
        }
      }

      this.$refs.ICropper.bind(bindOption).then(() => {
        if (bindOption.points) {
          this.cropperZoom = this.$refs.ICropper.getCropArea().percent
        } else {
          // 沒有辨識出人臉或指定位置，將 zoom 設定為最小
          this.cropperZoom = 0
          this.$refs.ICropper.setZoomViaPercent(0)
        }
      })
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~css/addon/colors'
@import '~css/addon/main'

.avatar-editor {
  position: relative
  max-width: 100%
  width: 600px
  // max-height: 360px
  border-radius: 10px
  background: $white

  button {
    background: transparent
  }

  &-header {
    display: flex
    padding: 20px
    justify-content: space-between
    align-items: center
  }

  &-title {
    font-size: 1.25rem

    > h3 {
      font-size: inherit
      text-shadow: none
      color: $black
    }
  }

  &-body {
    display: flex
    justify-content: center
    align-items: center
  }

  &-footer {
    padding: 10px 20px
    display: flex
    justify-content: space-between
    align-items: center

  }

  .upload-image-btn {
    background-color: $pink-color
    color: $white
    border-radius: 5px
    font-size: 1rem
    padding: 8px 12px
  }

  .change-image-btn {
    border: 1px solid $pink-color
    color: $pink-color
    border-radius: 5px
    font-size: 1rem
    padding: 8px 12px
  }

  .delete-image-btn {
  }

  .upload-container {
    padding-bottom: 20px
  }
}

#avatar-file {
  display: none
}

.cropper-slider-wrapper {
  position: absolute
  display: flex
  width: 100%
  bottom: 0
  align-items: center
  justify-content: center
  font-weight: 300
  height: 20px
  background-color: $black
  z-index: 1

  .cropper-percent {
    margin: 0 1rem
    width: 1.5rem
    color: $white
  }
}
</style>
